import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react'
import { createPortal } from 'react-dom'
import {
  useFloating,
  useTransitionStyles,
  autoUpdate,
  flip,
  offset as offsetMiddleware,
  shift,
  size,
  type Placement,
} from '@floating-ui/react'

export type PopoverProps = {
  /** Trigger element. Clicking it toggles the popover. Must accept `ref` + `onClick`. */
  trigger: ReactElement
  children: ReactNode
  /**
   * Preferred placement relative to the trigger. Default `bottom-start`.
   * The library auto-flips to the opposite side, slides along the trigger
   * axis to stay in viewport, and caps width/height to available space —
   * so the prop is a hint, not a hard requirement.
   */
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  /** Gap between trigger and popover in px. Default 6. */
  offset?: number
  /** Called with the current open state when it changes. */
  onOpenChange?: (open: boolean) => void
  /** Start open (rarely useful — prefer controlling via trigger click). */
  defaultOpen?: boolean
  /**
   * Optional controlled `open` flag. When provided the parent fully owns
   * the open state and must update it via `onOpenChange`. Leave undefined
   * for the default uncontrolled behavior.
   */
  open?: boolean
}

type TriggerElement = ReactElement<{
  ref?: Ref<HTMLElement>
  onClick?: (event: MouseEvent<HTMLElement>) => void
  'aria-haspopup'?: string
  'aria-expanded'?: boolean
  'aria-controls'?: string
}>

/**
 * Unstyled popover primitive. Anchors a floating panel to an arbitrary trigger.
 * Closes on outside-click or Escape; re-positions on scroll/resize/layout shift.
 *
 * Positioning is delegated to `@floating-ui/react` middlewares — `flip` for
 * vertical + horizontal axis flips, `shift` to keep the panel in viewport,
 * `size` to dynamically cap width/height to available space (overflow scrolls
 * internally so a tall popover near a viewport edge always remains fully
 * visible). The caller's `placement` is a *preference*; the library picks
 * the best actual placement based on real-time measurements, so popovers
 * triggered with a sidebar open / on a small viewport / near any edge
 * never bleed off-screen.
 *
 * Use this for filter panels, action menus, and small flyouts. For longer
 * content (forms, details) use <SidePanel> instead.
 */
export function Popover({
  trigger,
  children,
  placement = 'bottom-start',
  offset = 6,
  onOpenChange,
  defaultOpen = false,
  open: controlledOpen,
}: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const triggerRef = useRef<HTMLElement | null>(null)
  const wasOpenRef = useRef(defaultOpen)
  const panelId = useId()

  const setOpenWithNotify = (next: boolean) => {
    if (!isControlled) setInternalOpen(next)
    onOpenChange?.(next)
  }

  // `strategy: 'fixed'` matches the prior `position: fixed` behaviour so
  // popovers anchored via the body portal stay anchored to the viewport
  // (not to whichever ancestor establishes a containing block).
  const { refs, floatingStyles, isPositioned, context } = useFloating({
    placement: placement as Placement,
    strategy: 'fixed',
    // Position via top/left (not transform) so `transform` is free for the
    // open/close scale+slide animation below — otherwise the animation's
    // transform would clobber Floating UI's positioning transform.
    transform: false,
    open,
    whileElementsMounted: autoUpdate,
    middleware: [
      offsetMiddleware(offset),
      // Try the opposite side if the preferred one doesn't fit, and try
      // the orthogonal-axis variant (start ↔ end) before giving up.
      // Covers popovers anchored near corners — e.g. Cobrar at `top-end`
      // near the right edge flips to `top-start`.
      flip({ fallbackAxisSideDirection: 'end' }),
      // Slide along the trigger axis to keep the panel within the viewport
      // when the preferred placement would overflow.
      shift({ padding: 8 }),
      // Cap width + height to available space and let the panel scroll
      // internally — a tall Cobrar popover (e.g. Por-jugador mode with 4
      // slots) on a short viewport stays fully reachable.
      size({
        padding: 8,
        apply({ availableWidth, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${Math.max(220, Math.floor(availableWidth))}px`,
            maxHeight: `${Math.max(120, Math.floor(availableHeight))}px`,
            overflowY: 'auto',
          })
        },
      }),
    ],
  })

  // Enter/exit animation — fade + a subtle scale/slide. `useTransitionStyles`
  // keeps the panel mounted through the CLOSE animation (a plain `open &&`
  // unmount can't animate out), and `initial: { opacity: 0 }` also covers the
  // first unpositioned frame. Durations stay in the 100–150ms micro-interaction
  // band (per the UI guidelines).
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: { open: 150, close: 110 },
    initial: { opacity: 0, transform: 'scale(0.97) translateY(-3px)' },
  })

  // Wire the trigger element to BOTH our local ref (for focus-restore) and
  // Floating UI's reference handle. `refs.setReference` is stable across
  // renders so the callback can be too.
  const setTriggerRef = useCallback(
    (node: HTMLElement | null) => {
      triggerRef.current = node
      refs.setReference(node)
    },
    [refs],
  )

  // Restore focus to the trigger when the popover closes after being open
  // (WCAG 2.4.3). Skip the initial mount when open is false.
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true
      return
    }
    if (wasOpenRef.current && triggerRef.current && document.body.contains(triggerRef.current)) {
      triggerRef.current.focus()
      wasOpenRef.current = false
    }
  }, [open])

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return
    const onDocClick = (e: globalThis.MouseEvent) => {
      const t = e.target as Node | null
      if (refs.floating.current?.contains(t ?? null)) return
      if (triggerRef.current?.contains(t ?? null)) return
      // Ignore clicks inside a nested modal/alert-dialog portal — e.g. a
      // ConfirmDialog opened from inside the popover. Without this, the
      // dialog's Reembolsar button would close the popover BEFORE its
      // onClick fires, dropping the action silently.
      if (t instanceof Element && t.closest('[role="alertdialog"], [role="dialog"].modal')) {
        return
      }
      setOpenWithNotify(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenWithNotify(false)
    }
    document.addEventListener('mousedown', onDocClick)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      window.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!isValidElement(trigger)) {
    throw new Error('Popover: `trigger` must be a valid React element')
  }
  const triggerEl = trigger as TriggerElement

  const renderedTrigger = cloneElement(triggerEl, {
    ref: setTriggerRef,
    onClick: (e: MouseEvent<HTMLElement>) => {
      triggerEl.props.onClick?.(e)
      setOpenWithNotify(!open)
    },
    'aria-haspopup': 'true',
    'aria-expanded': open,
    'aria-controls': panelId,
  })

  // Portal the popover panel into <body> so its `position: fixed` is
  // anchored to the viewport, not to whichever ancestor happens to have
  // a `transform` / `filter` / `backdrop-filter` set (which would create
  // a new containing block and reduce `fixed` to "fixed within that
  // ancestor"). Without the portal, popovers triggered inside a `.panel`
  // — e.g. the TimeField selector inside Settings → Horario — get
  // clipped by the panel's `overflow: hidden` because `.panel`'s
  // entry-animation applies `transform: translateY(0)` at rest.
  return (
    <>
      {renderedTrigger}
      {isMounted &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            id={panelId}
            ref={refs.setFloating}
            role="dialog"
            className="popover"
            style={{
              ...floatingStyles,
              // Animation styles (opacity + transform scale/slide). Positioning
              // is now top/left (transform:false) so this transform is free.
              ...transitionStyles,
              // Hidden until Floating UI has measured the trigger + computed the
              // real position. Without this the panel paints once at top-left
              // (0,0) before the first measurement lands, then jumps to the
              // anchor — the "appears top-left then snaps" flash. `isPositioned`
              // flips true after the first computePosition, so the panel only
              // ever becomes visible already in the right place. Still in the
              // DOM while hidden (measurable), so positioning can run.
              visibility: isPositioned ? 'visible' : 'hidden',
              // Sit above drawer (95) + modal scrim (100) so popovers
              // opened inside a side panel render above the panel itself.
              zIndex: 200,
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  )
}
